import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SdfCheckbox } from '@waypoint/react-components';
import { resolveAriaProperty } from '../util/resolveAriaProperty';

export interface ICheckbox2Props {
  'aria-describedby'?: string;
  ariaDescribedby?: string;
  'aria-invalid'?: boolean;
  ariaInvalid?: boolean;
  'aria-label'?: string;
  ariaLabel?: string;
  'aria-required'?: boolean;
  ariaRequired?: boolean;
  autoFocus?: boolean;
  checked?: boolean;
  children?: React.ReactNode;
  className?: string;
  defaultChecked?: boolean;
  description?: string;
  disabled?: boolean;
  excludeFromForm?: boolean;
  expandHitArea?: boolean;
  hideLabel?: boolean;
  id?: string;
  indeterminate?: boolean;
  inline?: boolean;
  name?: string;
  required?: boolean;
  tabIndex?: number;
  value?: string;
  onBlur?: (event?) => void;
  onChange?: (checked: boolean) => void;
  onFocus?: (event?) => void;
}

export const Checkbox2 = forwardRef<HTMLSdfCheckboxElement, ICheckbox2Props>((props, ref) => {
  const {
    autoFocus,
    checked: controlledChecked,
    children,
    className,
    defaultChecked = false,
    description,
    disabled,
    excludeFromForm,
    expandHitArea,
    hideLabel,
    id,
    indeterminate,
    inline,
    name,
    required,
    tabIndex,
    value,
    onBlur,
    onChange,
    onFocus
  } = props;

  const sdfRef = useRef<HTMLSdfCheckboxElement>(null);
  const isControlled = controlledChecked !== undefined;
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked);
  const checked = isControlled ? controlledChecked : uncontrolledChecked;
  const resolvedAriaDescribedby = resolveAriaProperty('Checkbox2', 'aria-describedby', 'ariaDescribedby', props);
  const resolvedAriaInvalid = resolveAriaProperty('Checkbox2', 'aria-invalid', 'ariaInvalid', props);
  const resolvedAriaLabel = resolveAriaProperty('Checkbox2', 'aria-label', 'ariaLabel', props);
  const resolvedAriaRequired = resolveAriaProperty('Checkbox2', 'aria-required', 'ariaRequired', props);

  useImperativeHandle(ref, () => sdfRef.current);

  useEffect(() => {
    if (autoFocus) {
      void sdfRef.current?.setFocus();
    }
  }, []);

  const handleChange = (event: CustomEvent<boolean>) => {
    const newValue = event.detail;

    if (!isControlled) {
      setUncontrolledChecked(newValue);
    }

    onChange?.(newValue);
  };

  const handleFocus = (event) => {
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    onBlur?.(event);
  };

  // Stencil's React wrapper has a bug where boolean camelCase props render as
  // attribute="false" instead of removing the attribute. Convert false to undefined
  // so the attribute is removed. The 'checked' prop is excluded from this treatment
  // because the web component needs to see false to uncheck.
  const toBoolProp = (val: boolean | undefined) => val || undefined;

  const sdfProps: any = {
    'aria-describedby': resolvedAriaDescribedby,
    'aria-invalid': resolvedAriaInvalid,
    'aria-label': resolvedAriaLabel,
    'aria-required': resolvedAriaRequired || required,
    checked,
    class: className,
    description,
    disabled: toBoolProp(disabled),
    excludeFromForm: toBoolProp(excludeFromForm),
    expandHitArea: toBoolProp(expandHitArea),
    hideLabel: toBoolProp(hideLabel),
    id,
    indeterminate: toBoolProp(indeterminate),
    name,
    required: toBoolProp(required),
    tabIndex,
    value
  };

  Object.keys(sdfProps).forEach((key) => {
    if (sdfProps[key] === undefined) {
      delete sdfProps[key];
    }
  });

  const checkbox = (
    <SdfCheckbox
      ref={sdfRef}
      {...sdfProps}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onSdfChange={handleChange}
    >
      {children}
    </SdfCheckbox>
  );

  if (inline) {
    return <span style={{ display: 'inline-block' }}>{checkbox}</span>;
  }

  return checkbox;
});

Checkbox2.displayName = 'Checkbox2';
